import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useLogin }      from '../../hooks/useLogin';
import FormInput         from '../../components/ui/FormInput';
import Button            from '../../components/ui/Button';
import { ROUTES }        from '../../constants/api';

import './LoginPage.css';

/* ---- SVG Icons ------------------------------------------------ */
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const IconGoogle = () => (
  <svg className="login-inline-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const IconArrowLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const IconLogIn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const IconKey = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

/* ==============================================================
   LOGIN PAGE — Simple centered card
   ============================================================== */
export default function LoginPage() {
  const navigate = useNavigate();

  const {
    form,
    errors,
    globalError,
    isLoading,
    showPassword,
    handleChange,
    handleSubmit,
    togglePassword,
  } = useLogin(() => navigate(ROUTES.DASHBOARD, { replace: true }));

  return (
    <main className="login-page" role="main">
      <Link to={ROUTES.MAIN} className="login-page__back">
        <IconArrowLeft />
        <span>Bosh sahifa</span>
      </Link>

      <div className="login-card" role="region" aria-label="Sign in">

        {/* Brand */}
        <div className="login-card__brand">
          <div className="login-card__logo-mark" aria-hidden="true">U</div>
          <span className="login-card__brand-name">Uday Platform</span>
        </div>

        {/* Heading */}
        <div className="login-card__heading">
          <h1 className="login-card__title">
            Qayta <em>xush kelibsiz</em>
          </h1>
          <p className="login-card__subtitle">
            Admin paneliga kiring
          </p>
        </div>

        {/* Global error */}
        {globalError && (
          <div className="login-global-error" role="alert" aria-live="assertive">
            <span className="login-global-error__icon"><IconAlert /></span>
            {globalError}
          </div>
        )}

        {/* Form */}
        <form
          id="admin-login-form"
          onSubmit={handleSubmit}
          noValidate
          aria-label="Admin sign in form"
        >
          <div className="login-card__fields">
            <FormInput
              id="login-phone"
              name="phone"
              label="Telefon raqami"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="+998 90 123 45 67"
              error={errors.phone}
              disabled={isLoading}
              autoComplete="tel"
              autoFocus
              iconLeft={<IconUser />}
            />

            <FormInput
              id="login-password"
              name="password"
              label="Parol"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              placeholder="Parolingizni kiriting"
              error={errors.password}
              disabled={isLoading}
              autoComplete="current-password"
              iconLeft={<IconLock />}
              iconRight={showPassword ? <IconEyeOff /> : <IconEye />}
              onIconRightClick={togglePassword}
              iconRightLabel={showPassword ? 'Hide password' : 'Show password'}
            />
          </div>

          {/* Options */}
          <div className="login-card__options">
            <span
              className="login-action-link"
              title="Tez orada"
            >
              <IconKey />
              Parolni unutdingizmi?
            </span>
            <span
              className="login-action-link"
              title="Tez orada"
            >
              <IconGoogle />
              Google orqali kirish
            </span>
          </div>

          {/* Submit */}
          <div className="login-card__submit">
            <Button
              id="login-submit-btn"
              type="submit"
              variant="primary"
              size="md"
              isLoading={isLoading}
              iconLeft={!isLoading ? <IconLogIn /> : undefined}
              style={{ width: '100%' }}
            >
              {isLoading ? 'Kirilmoqda…' : 'Kirish'}
            </Button>
          </div>
        </form>

      </div>
    </main>
  );
}
